import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink, MapPin, Star } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import {
  aspectLabel,
  domainLabel,
  HORIZON_LABELS,
  impactAxisLabel,
  POLARITY_LABELS,
  polarityTone,
} from "@/lib/accountSignals"

type TradeSignal = {
  id: number
  domain: string
  aspect: string | null
  title: string
  body: string | null
  polarity: string | null
  horizon: string | null
  impact_axis: string | null
  magnitude: number | null
  confidence: number | null
  geo_place: string | null
  source_url: string | null
  source_date: string | null
}

type TradeGroup = {
  id: number
  name: string
  code: string | null
  is_key_account: boolean
  pic_name: string | null
  signal_count: number
  signals: TradeSignal[]
}

type PageProps = {
  groups: TradeGroup[]
  summary: { account_count: number; signal_count: number }
  filters: { domain: string | null; place: string | null }
  filter_options: { domains: string[]; places: string[] }
}

const DESCRIPTION =
  "Surface Sales & Trade Marketing: sinyal akun yang disetujui pada domain perdagangan (harga/promosi, kategori, ekspansi, omnichannel, rantai pasok), dikelompokkan per akun untuk JBP, negosiasi promo, dan perencanaan demand regional."

export default function TradeMarketing({ groups, summary, filters, filter_options }: PageProps) {
  const applyFilters = (next: { domain?: string; place?: string }) => {
    const merged = {
      domain: filters.domain ?? "",
      place: filters.place ?? "",
      ...next,
    }
    const query: Record<string, string> = {}
    Object.entries(merged).forEach(([k, v]) => {
      if (v != null && v !== "") query[k] = String(v)
    })
    router.get("/admin/accounts/trade-marketing", query, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  return (
    <>
      <Head title="Trade Marketing">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Trade Marketing" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts">Account</Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Trade Marketing</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Trade Marketing</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>{summary.account_count} akun</span>
            <span>{summary.signal_count} sinyal perdagangan</span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[200px]">
            <label htmlFor="domain">Domain</label>
            <Select
              id="domain"
              className="mt-1"
              value={filters.domain ?? ""}
              onChange={(e) => applyFilters({ domain: e.target.value })}
            >
              <option value="">Semua domain</option>
              {filter_options.domains.map((d) => (
                <option key={d} value={d}>
                  {domainLabel(d)}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[200px]">
            <label htmlFor="place">Wilayah</label>
            <Select
              id="place"
              className="mt-1"
              value={filters.place ?? ""}
              onChange={(e) => applyFilters({ place: e.target.value })}
            >
              <option value="">Semua wilayah</option>
              {filter_options.places.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              Belum ada sinyal perdagangan yang disetujui untuk filter ini.
            </p>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            {groups.map((g) => (
              <section key={g.id} className="rounded-lg border border-hairline bg-surface">
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Link href={`/admin/accounts/${g.id}`} className="font-medium">
                      {g.name}
                    </Link>
                    {g.is_key_account && (
                      <Badge tone="accent">
                        <Star className="mr-1 h-3 w-3" />KA
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-ink-muted">
                    {g.pic_name && <span>PIC: {g.pic_name}</span>}
                    <Badge tone="neutral">{g.signal_count} sinyal</Badge>
                  </div>
                </header>
                <ul className="divide-y divide-hairline">
                  {g.signals.map((s) => (
                    <li key={s.id} className="px-4 py-3">
                      <TradeSignalRow signal={s} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </AppShell>
    </>
  )
}

function TradeSignalRow({ signal }: { signal: TradeSignal }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="neutral">{domainLabel(signal.domain)}</Badge>
        {signal.aspect && <Badge tone="muted">{aspectLabel(signal.aspect)}</Badge>}
        {signal.polarity && (
          <Badge tone={polarityTone(signal.polarity)}>
            {POLARITY_LABELS[signal.polarity] ?? signal.polarity}
          </Badge>
        )}
        {signal.horizon && <Badge tone="signal">{HORIZON_LABELS[signal.horizon] ?? signal.horizon}</Badge>}
      </div>
      <p className="mt-1.5 font-medium text-ink-body">{signal.title}</p>
      {signal.body && <p className="mt-0.5 text-sm text-ink-muted">{signal.body}</p>}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
        {signal.geo_place && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {signal.geo_place}
          </span>
        )}
        {signal.impact_axis && <span>Dampak: {impactAxisLabel(signal.impact_axis)}</span>}
        {signal.magnitude != null && (
          <span>
            M{signal.magnitude}
            {signal.confidence != null && ` · K${signal.confidence.toFixed(2)}`}
          </span>
        )}
        {signal.source_date && <span>{signal.source_date}</span>}
        {signal.source_url && (
          <a
            href={signal.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1"
          >
            Sumber <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  )
}
